import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Button,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export function ContactPage() {
  const location = useLocation();

  const query = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      plan: params.get('plan') || '',
    };
  }, [location.search]);

  const [plan, setPlan] = useState(query.plan);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);

  const canSend = name.trim() && email.trim() && message.trim();

  const mailtoHref = useMemo(() => {
    const subject = encodeURIComponent(`DZ Content Factory Anfrage${plan ? ` (${plan})` : ''}`);
    const body = encodeURIComponent(
      `Name: ${name}\nFirma: ${company}\nE-Mail: ${email}\nInteresse: ${plan || '-'}\n\nNachricht:\n${message}`
    );
    return `mailto:info@dikitalsolutions.com?subject=${subject}&body=${body}`;
  }, [company, email, message, name, plan]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a1929' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'rgba(10, 25, 41, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 180, 216, 0.1)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component={Link}
                to="/"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textDecoration: 'none',
                }}
              >
                <TimelineIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                DZ Content Factory
              </Typography>
            </Box>

            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBackIcon />}
              sx={{ color: '#e2e8f0' }}
            >
              Zurück
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="md" sx={{ pt: 14, pb: 8 }}>
        <Paper
          sx={{
            p: { xs: 3, md: 6 },
            background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
            border: '1px solid rgba(0, 180, 216, 0.15)',
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#e2e8f0', mb: 2 }}>
            Anfrage
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Sag kurz, was du dokumentieren möchtest. Wir melden uns zeitnah mit einer passenden Empfehlung (Pilot Light, Credits oder Enterprise).
          </Typography>

          <Box component="form" noValidate autoComplete="off">
            <TextField
              fullWidth
              label="Interesse"
              select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Bitte wählen</MenuItem>
              <MenuItem value="Pilot Light">Pilot Light</MenuItem>
              <MenuItem value="Credits">Credits</MenuItem>
              <MenuItem value="Enterprise">Enterprise</MenuItem>
              <MenuItem value="Pilot">Pilot</MenuItem>
              <MenuItem value="Professional">Professional</MenuItem>
            </TextField>

            <GridLike>
              <TextField
                fullWidth
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
              />
              <TextField
                fullWidth
                label="Firma (optional)"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </GridLike>

            <TextField
              fullWidth
              label="E-Mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              sx={{ mt: 2 }}
            />

            <TextField
              fullWidth
              label="Nachricht"
              placeholder="z.B. 1 Standort, 60 Sensoren, jährlicher Audit + 1 Umbau"
              multiline
              minRows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onBlur={() => setTouched(true)}
              sx={{ mt: 2 }}
            />

            {touched && !canSend && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Bitte Name, E-Mail und Nachricht ausfüllen.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              <Button
                component="a"
                href={mailtoHref}
                variant="contained"
                disabled={!canSend}
                sx={{
                  background: 'linear-gradient(135deg, #14b8a6, #00b4d8)',
                  '&:hover': { background: 'linear-gradient(135deg, #2dd4bf, #00d9ff)' },
                }}
              >
                Anfrage per E-Mail senden
              </Button>
              <Button component={Link} to="/" variant="outlined">
                Zur Startseite
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Alternativ direkt: info@dikitalsolutions.com · +49 365 773 184 89
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

function GridLike({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
      }}
    >
      {children}
    </Box>
  );
}
